<template>
   <v-container fluid align-start ma-0 pa-2 class="epms5060-main container-height">
    <v-row no-gutters>
      <v-col cols="12">
        <i-card-vertical
          :headerTitle="menuTitle"
          :useBtnList="useBtnList"
          @btnRetrieve="btnRetrieve"
          @btnSave="btnSave"
          @btnExcel="btnExcel"
          @btnUpload="btnUpload"
        >
          <template v-slot:body>
            <v-row no-gutters>
              <v-col>
                <i-card-top
                  class="d-md-flex"
                  outlined
                >
                  <template v-slot:body>
                    <v-row no-gutters class="center pa-2 pb-0">
                      <i-label :label="'Ship No :'" />
                      <v-col cols="1">
                        <v-autocomplete
                          v-model="selectShipNo"
                          :items="shipNoList"
                          return-object
                          item-text="shipNo"
                          item-value="shipNo"
                          single-line
                          outlined
                          dense
                          height="32"
                          @change="changeShipNo" />
                      </v-col>

                      <i-label :label="'Block :'" class="pl-2" />
                      <v-col cols="1">
                        <v-autocomplete
                          v-model="selectBlock"
                          :items="blockList"
                          return-object
                          item-text="block"
                          item-value="block"
                          single-line
                          outlined
                          dense
                          height="32" />
                      </v-col>

                      <i-label :label="'POR No :'" class="pl-2" />
                      <v-col cols="1">
                        <v-autocomplete
                          v-model="selectPorNo"
                          :items="porNoList"
                          return-object
                          item-text="porNo"
                          item-value="porNo"
                          single-line
                          outlined
                          dense
                          height="32" />
                      </v-col>

                      <i-label :label="'Items :'" class="pl-2" />
                      <v-col cols="2">
                        <v-autocomplete
                          v-model="selectItems"
                          :items="itemsList"
                          return-object
                          item-text="prtSpec"
                          item-value="prtGrp"
                          single-line
                          outlined
                          dense
                          height="32" />
                      </v-col>

                      <i-label :label="'Part No :'" class="pl-2" />
                      <v-col cols="1">
                        <v-text-field 
                          v-model="partNo"
                          dense 
                          outlined 
                          hide-details="auto"
                          height="32"
                        />
                      </v-col>
                    </v-row>

                    <v-row no-gutters class="center pa-2">
                      <i-label :label="'Day Division :'" />
                      <v-col cols="2">
                        <v-select
                          v-model="selectDayDiv"
                          :items="dayDivList"
                          return-object
                          item-text="codeName"
                          item-value="code"
                          single-line
                          outlined
                          dense
                          height="32" />
                      </v-col>
                      
                      <v-col cols="1" class="ml-2">
                        <DxDateBox
                          :value="stDte"
                          height="32px"
                          display-format="yyyy-MM-dd"
                          :acceptCustomValue="false"
                          @value-changed="e => {stDte = e.value && getStrFormat(e.value)}"
                        />
                      </v-col>
                      <span class="px-2 d-flex align-center text-h6"> ~ </span>
                      <v-col cols="1">
                        <DxDateBox
                          :value="edDte"
                          height="32px"
                          display-format="yyyy-MM-dd"
                          :acceptCustomValue="false"
                          @value-changed="e => {edDte = e.value && getStrFormat(e.value)}"
                        />
                      </v-col>

                      <v-checkbox v-model="isUrgent" label="Urgent" class="ml-2 check__urgent pl-1 pr-2" true-value="Y" false-value="" />
                      <v-checkbox v-model="isRevisePor" label="Revise POR" class="ml-2 check__revise-por pl-1 pr-2" true-value="Y" false-value="N" />
                    </v-row>
                  </template>
                </i-card-top>
              </v-col>
            </v-row>

            <v-row no-gutters class="pt-1">
              <v-col>
                <DxDataGrid
                  class="hipGridHeight"
                  :ref="epms5060Ref"
                  :data-source="dataList" 
                  :remote-operations="false" 
                  :hover-state-enabled="true"
                  :allow-column-resizing="true" 
                  :allow-column-reordering="true"
                  :row-alternation-enabled="true" 
                  :show-borders="true" 
                  :width="grid.width"
                  :show-row-lines="true" 
                  key-expr="id" 
                  :focused-row-enabled="false"
                  :column-hiding-enabled="false" 
                  :selection="{ mode: 'none' }"
                  :sorting="{ mode: 'none' }"
                  @cellPrepared="cellPrepared"
                  @row-updated="onRowUpdated"
                >
                  <DxPaging :enabled="true" :page-size="setPage" />
                  <DxPager :show-page-size-selector="false" :show-info="false" />
                  <DxEditing :allow-updating="true" />

                  <DxColumn caption="No" data-field="rnum" width="3%" alignment="center" :allow-editing="false" />

                  <DxColumn caption="Ship No">
                    <DxColumn caption="POR No" data-field="shipNo" width="7%" alignment="center" :allow-editing="false"
                                          cell-template="shipPorTmpl" />
                  </DxColumn>
                  <template #shipPorTmpl="{ data }">
                    {{ data.data.gbn === 1 ? data.data.shipNo : `${data.data.porNo}-${data.data.porSeq}` }}
                  </template>

                  <DxColumn caption="Block" data-field="block" width="4%" alignment="center" :allow-editing="false" />

                  <DxColumn caption="Activity">
                    <DxColumn caption="Part Specification" data-field="mact" width="8%" alignment="center" :allow-editing="false"
                                          cell-template="actPartTmpl" />
                  </DxColumn>
                  <template #actPartTmpl="{ data }">
                    {{ data.data.gbn === 1 ? data.data.mact : data.data.partSpec }}
                  </template>

                  <DxColumn caption="BOM SerNo">
                    <DxColumn caption="Part Specification1" data-field="bomSerno" width="6%" alignment="center" :allow-editing="false" />
                  </DxColumn>

                  <DxColumn caption="Part No">
                    <DxColumn caption="Part Specification2" data-field="partNo" width="8%" alignment="center" :allow-editing="false" />
                  </DxColumn>

                  <DxColumn caption="Part Qty">
                    <DxColumn caption="Part Specification3" data-field="partQty" width="5%" alignment="right" :allow-editing="false"
                                          data-type="number" format="#,##0" />
                  </DxColumn>

                  <DxColumn caption="Remain" data-field="remainQty" width="4%" alignment="right" :allow-editing="false"
                                        data-type="number" format="#,##0" />

                  <DxColumn caption="Make Start">
                    <DxColumn caption="Make Finish" data-field="stMakeDte" width="7%" alignment="center" :allow-editing="false"
                                          cell-template="makeDteTmpl" />
                  </DxColumn>
                  <template #makeDteTmpl="{ data }">
                    {{ data.data.gbn === 1 ? data.data.stMakeDte : data.data.edMakeDte }}
                  </template>

                  <DxColumn caption="Paint Start">
                    <DxColumn caption="Paint Finish" data-field="stPaintDte" width="7%" alignment="center" :allow-editing="false"
                                          cell-template="paintDteTmpl" />
                  </DxColumn>
                  <template #paintDteTmpl="{ data }">
                    {{ data.data.gbn === 1 ? data.data.stPaintDte : data.data.edPaintDte }}
                  </template>

                  <DxColumn caption="M.N.D">
                    <DxColumn caption="Req. Date" data-field="wkstpldt" width="7%" alignment="center" :allow-editing="false"
                                          header-cell-template="reqDateTmpl" cell-template="wkstpldtTmpl" />
                  </DxColumn>
                  <template #reqDateTmpl>
                    <div class="click-label d-flex align-center" @click.stop="setReqDate()">
                      <v-icon size="16" color="orange" class="pr-1">
                        mdi-arrow-right-box
                      </v-icon>
                      Req. Date
                    </div>
                  </template>
                  <template #wkstpldtTmpl="{ data }">
                    {{ data.data.gbn === 1 ? data.data.wkstpldt : data.data.reqIssueDt }}
                  </template>

                  <DxColumn data-field="dlyPlanDte" width="8%" alignment="center" :allow-editing="true"
                                        data-type="date" format="yyyy-MM-dd"
                                        header-cell-template="possibleDeliveryDateTmpl"
                                        :set-cell-value="dlyPlanDteSelect" />
                  <template #possibleDeliveryDateTmpl>
                    <fragment>
                      <div class="click-label" @click.stop="setAllDate()">
                        <div class="d-flex align-center justify-center">
                          <v-icon size="16" color="orange" class="pr-1">
                            mdi-arrow-right-box
                          </v-icon>
                          Possible
                        </div>
                        <div>
                          Delivery Date
                        </div>
                      </div>
                    </fragment>
                  </template>

                  <DxColumn caption="Inspection Date" data-field="inspDte" width="8%" alignment="center" :allow-editing="true" 
                                        data-type="date" format="yyyy-MM-dd"
                                        header-cell-template="InspectionDateTmpl"
                                        :set-cell-value="inspDteSelect" />
                  <template #InspectionDateTmpl>
                    <div class="click-label d-flex align-center" @click.stop="setInsDate()">
                      <v-icon size="16" color="orange" class="pr-1">
                        mdi-arrow-right-box
                      </v-icon>
                      Inspection Date
                    </div>
                  </template>
                  
                  <DxColumn caption="Remark" data-field="remarks" :allow-editing="true"/>

                  <DxColumn data-field="reqIssueDt" :visible="false" />
                </DxDataGrid>
              </v-col>
            </v-row>
          </template>
        </i-card-vertical>
      </v-col>
    </v-row>

    <saveAsExcelPop
      :showXlsImport.sync="showXlsImport"
      :param="xlsImportParam"
      :url="'/delivery/epms5060/epms5060Xls_Save'"
      @saved="btnRetrieve" />
   </v-container>
</template>

<script>
import baseview from '@/components/base/baseview.vue'
import BaseDataGrid from '@/components/base/BaseDataGrid.vue'
import DxDateBox from 'devextreme-vue/date-box'
import { getDateFormat, getCurrentDate } from '@/utils/common.js'
import { getCmChildCode } from '@/api/system/cmCodeManage'
import { epms5060Items, epms5060, epms5060Save, epms5060Xls } from '@/api/view/hip/5/epms5060'
import { getShipNoList1, epms9000_block, epms9000_por_no } from '@/api/view/hip/common/common.js'
import notify from 'devextreme/ui/notify'
import saveAsExcelPop from '@/views/hip/common/saveAsExcelPop'

export default {
  name: 'epms5060',
  mixins: [baseview, BaseDataGrid],
  components: {
    DxDateBox,
    saveAsExcelPop
  },
  data() {
    return {
      menuTitle: this.$route.meta.title,
      makerCd: this.$store.state.user.makerCd,
      userId: this.$store.state.user.userId,
      userIp: this.$store.state.user.userIp,
      epms5060Ref: 'epms5060Ref',
      useBtnList: ['btnRetrieve', 'btnSave', 'btnExcel', 'btnUpload'],
      showXlsImport: false,
      xlsImportParam: {},
      dataList: [],
      selectShipNo: null,
      shipNoList: [],
      selectBlock: null,
      blockList: [],
      selectPorNo: null,
      porNoList: [],
      selectItems: null,
      itemsList: [],
      selectDayDiv: null,
      dayDivList: [],
      saveRows: [],
      partNo: '',
      stDte: '',
      edDte: '',
      isUrgent: '',
      isRevisePor: 'N',
      setPage: 100
    }
  },
  computed: {
    gridInstance() {
      return this.GetDataGrid(this.epms5060Ref)
    }
  },
  methods: {
    init() {
      this.gridInstance.option('defaultPaging', false)
      this.stDte = getCurrentDate()
      this.edDte = getCurrentDate({ mm: 1 })
    },
    async getDayDlvList() {
      const param = {
        code: '',
        codegr: '',
        prtCode: 'DAY_DIV',
        userFlag: 'Y'
      }

      await getCmChildCode(param).then(res => {
        this.dayDivList = res.list
        this.selectDayDiv = this.dayDivList[0]
      })
    },
    async getShipNoList() {
      const param = {
        makerCd: this.makerCd
      }

      await getShipNoList1(param).then(async res => {
        // console.log('getShipNoList1Res', res)

        this.shipNoList = res.list
        this.selectShipNo = this.shipNoList[0]

        await this.getBlockList()
        await this.getPorNoList()
        await this.btnRetrieve()
      })
    },
    async getBlockList() {
      this.openLoading()

      const param = {
        incNotPo: 'Y',
        shipNo: this.selectShipNo.shipNo,
        makerCd: this.makerCd
      }

      await epms9000_block(param).then(res => {
        // console.log('epms9000_blockRes', res)

        this.blockList = res.list
        this.blockList.unshift({
          block: ''
        })
        this.selectBlock = this.blockList[0]
      }).finally(() => {
        this.endLoading()
      })
    },
    async getPorNoList() {
      this.openLoading()

      const param = {
        incNotPo: 'Y',
        shipNo: this.selectShipNo.shipNo,
        makerCd: this.makerCd
      }

      await epms9000_por_no(param).then(res => {
        // console.log('epms9000_blockRes', res)

        this.porNoList = res.list
        this.porNoList.unshift({
          porNo: ''
        })
        this.selectPorNo = this.porNoList[0]
      }).finally(() => {
        this.endLoading()
      })
    },
    async changeShipNo() {
      this.selectBlock = null
      this.blockList = []
      this.selectPorNo = null
      this.porNoList = []

      await this.getBlockList()
      await this.getPorNoList()
      await this.btnRetrieve()
    },
    getStrFormat(date) {
      return getDateFormat(date)
    },
    async getItems() {
      const param = {
        makerCd: this.makerCd
      }

      await epms5060Items(param).then(res => {
        // console.log('getItemsRes', res)

        this.itemsList = res.list
        this.selectItems = this.itemsList[0]
      })
    },
    btnRetrieve() {
      this.openLoading()
      this.dataList = []

      const param = {
        shipNo: this.selectShipNo.shipNo,
        block: this.selectBlock ? this.selectBlock.block : '',
        porNo: this.selectPorNo ? this.selectPorNo.porNo : '',
        itemNo: this.selectItems.prtGrp,
        partNo: this.partNo,
        dayDiv: this.selectDayDiv.code,
        makerCd: this.makerCd,
        urgentYn: this.isUrgent,
        reviseYn: this.isRevisePor,
        stDte: this.stDte,
        edDte: this.edDte
      }

      epms5060(param).then(res => {
        // console.log('epms5060Res', res)

        let result = res.list
        let makeData = []
        let id = 0

        result.forEach(row => {
          id++
          let row1 = JSON.parse(JSON.stringify(row))
          row1.id = id
          row1.gbn = 1
          makeData.push(row1)

          id++
          let row2 = JSON.parse(JSON.stringify(row))
          row2.id = id
          row2.gbn = 2
          makeData.push(row2)
        })

        this.dataList = makeData
      }).finally(() => {
        this.endLoading()
      })
    },
    onRowUpdated(e){      
      this.saveRows.push(e)     
    },
    btnSave() {
      if(this.dataList.length < 1) {
        this.vToastify(this.$t('Data is not retrieved.'), 'warn')
        return
      }

      this.vToastifyPrompt(
        this.$t('Do you want to save the delivery plan information?'),
        'info',
        async current => {
          await this.gridInstance.saveEditData()
          
          let saveData = []
          //const currRows = this.gridInstance.getVisibleRows()
          const currRows = this.saveRows
          console.log('currRows', currRows)                    
          
          const allVisible = currRows.length === this.gridInstance.getVisibleRows().length;

          for (let i = 0; i < currRows.length; i++) {
              if (!allVisible || i % 2 === 0) {
                  saveData.push(currRows[i].data);
              }
          }

          saveData.forEach(row => {
            row.userId = this.userId
            row.userIp = this.userIp
          })

          console.log('saveData', saveData)
          
          this.openLoading()
          epms5060Save(saveData).then(res => {
             const msg = res.body
            if(msg === 'OK') {
              notify(this.$t('Delivery Plan Information is saved.'), 'success', 3000)
              this.btnRetrieve()
            } else {
              this.vToastify(this.$t(msg), 'warn')
              return
            }
          }).finally(() => {
            this.saveRows = []
            this.endLoading()
          })
        },
        null,
        true
      )
    },
    btnExcel() {
      this.vToastifyPrompt(
        this.$t('Would You create the Excel file?'),
        'info',
        current => {
          this.openLoading()

          const param = {
            shipNo: this.selectShipNo.shipNo,
            block: this.selectBlock ? this.selectBlock.block : '',
            porNo: this.selectPorNo ? this.selectPorNo.porNo : '',
            itemNo: this.selectItems.prtGrp,
            partNo: this.partNo,
            dayDiv: this.selectDayDiv.code,
            makerCd: this.makerCd,
            urgentYn: this.isUrgent,
            reviseYn: this.isRevisePor,
            stDte: this.stDte,
            edDte: this.edDte
          }

          this.openLoading()
          epms5060Xls(param).
          finally(() => {
            this.endLoading()
          })
        },
        null,
        true
      )
    },
    btnUpload() {
      if(!this.selectShipNo || !this.selectShipNo.shipNo) {
        this.vToastify(this.$t('Ship number is not selected.'), 'warn')
        return
      }

      // this.xlsImportParam = {
      //   shipNo: this.selectShipNo.shipNo,
      //   block: this.selectBlock ? this.selectBlock.block : '',
      //   porNo: this.selectPorNo ? this.selectPorNo.porNo : '',
      //   partNo: this.partNo
      // }

      this.showXlsImport = true
    },
    setReqDate() {
      if(this.dataList.length < 1) {
        this.vToastify(this.$t('Data is not retrieved.'), 'warn')
        return
      }

      this.vToastifyPrompt(
        this.$t('Do you want to input Delivery Plan Date from Request Date?'),
        'info',
        current => {
          const count = this.gridInstance.getVisibleRows().length
          for(let i = 0; i < count; i++) {
            const reqIssueDt = this.gridInstance.cellValue(i, 'reqIssueDt')
            this.gridInstance.cellValue(i, 'dlyPlanDte', reqIssueDt)
          }
        },
        null,
        false
      )
    },
    setAllDate() {
      if(this.dataList.length < 1) {
        this.vToastify(this.$t('Data is not retrieved.'), 'warn')
        return
      }

      if(!this.gridInstance.cellValue(0, 'dlyPlanDte')) {
        this.vToastify(this.$t('Select Delivery Plan Date at first row.'), 'warn')
        return
      }

      const count = this.gridInstance.getVisibleRows().length
      for(let i = 0; i < count; i++) {
        const dlyPlanDte = this.gridInstance.cellValue(0, 'dlyPlanDte')
        this.gridInstance.cellValue(i, 'dlyPlanDte', dlyPlanDte)
      }
    },
    setInsDate() {
      if(this.dataList.length < 1) {
        this.vToastify(this.$t('Data is not retrieved.'), 'warn')
        return
      }

      if(!this.gridInstance.cellValue(0, 'inspDte')) {
        this.vToastify(this.$t('Select Delivery Plan Date at first row.'), 'warn')
        return
      }

      const count = this.gridInstance.getVisibleRows().length
      for(let i = 0; i < count; i++) {
        const inspDte = this.gridInstance.cellValue(0, 'inspDte')
        this.gridInstance.cellValue(i, 'inspDte', inspDte)
      }
    },
    cellPrepared(e) {
      if(e.rowType === 'header') {
        const caption = e.column.caption
        
        if(caption && caption.indexOf('Part Specification') > -1) {
          if(caption === 'Part Specification') {
            e.cellElement.setAttribute('colspan', 4)
          } else {
            e.cellElement.style.display = 'none'
          }
        }

        if(caption === 'Part No' || caption === 'Part Specification' || caption === 'Remain' || caption === 'Make Start' || caption === 'Make Finish'
             || caption === 'Paint Start' || caption === 'Paint Finish' || caption === 'M.N.D') {
          e.cellElement.style.color = '#0000FF'
        }
      } else if(e.rowType === 'data') {
        if(e.rowType === 'data' && e.rowIndex >= 0) {
          if(e.column.dataField === 'rnum' || e.column.dataField === 'block' || e.column.dataField === 'remainQty'
              || e.column.dataField === 'dlyPlanDte' || e.column.dataField === 'inspDte' || e.column.dataField === 'remarks') {
            if(e.data.gbn === 1) {
              e.cellElement.setAttribute('rowspan', 2)
            } else {
              e.cellElement.style.display = 'none'
            }
          }

          if(e.column.dataField === 'mact') {
            if(e.data.gbn === 2) {
              e.cellElement.setAttribute('colspan', 4)
              e.cellElement.style.textAlign = 'left'
            }
          }

          if(e.column.dataField === 'bomSerno' || e.column.dataField === 'partNo' || e.column.dataField === 'partQty') {
            if(e.data.gbn === 2) {
              e.cellElement.style.display = 'none'
            }
          }

          if(e.column.dataField === 'mact') {
            if(e.data.gbn === 2) {
              e.cellElement.style.color = '#0000FF'
            }
          }

          if(e.column.dataField === 'partNo' || e.column.dataField === 'remainQty') {
            if(e.data.gbn === 1) {
              e.cellElement.style.color = '#0000FF'
            }
          }

          if(e.column.dataField === 'stMakeDte' || e.column.dataField === 'stPaintDte') {
            e.cellElement.style.fontWeight = 'bold'
          }

          if(e.column.dataField === 'wkstpldt') {
            if(e.data.gbn === 1) {
              e.cellElement.style.color = '#0000FF'
            } else {
              e.cellElement.style.fontWeight = 'bold'
            }
          }
        }
      }
    },
    dlyPlanDteSelect(newData , value , currData) {
      newData.dlyPlanDte = getDateFormat(value)
    },
    inspDteSelect(newData , value , currData) {
      newData.inspDte = getDateFormat(value)
    }
  },
  async mounted() {
    this.init()
    this.getDayDlvList()
    this.getItems()
    this.getShipNoList()
  }
}
</script>

<style lang="scss" scoped>
.epms5060-main {
  ::v-deep {
    [role=btnSave], [role=btnExcel] {
      .v-btn__content {
        div, i {
          color: #0000ff !important;
        }
      }
    }
  }

  ::v-deep.check__urgent, ::v-deep.check__revise-por  {
    .v-label {
      color: black;
      font-weight: bold;
    }
  }

  .check__urgent {
    background: #ff3399;
  }

  .check__revise-por {
    background: #ffff33;
  }

  .click-label {
    cursor: pointer;
  }
  .click-label:hover {
    color: #4980BC;
  }
}
</style>